import { useUseRequest } from './../composables/useRequest'
type DisplayInfo =
  | 'Continent'
  | 'Country'
  | 'City'
  | 'Province'
  | 'Seaport'
  | 'Airport'

type AppointSearchName =
  | 'continentId'
  | 'countryId'
  | 'cityId'
  | 'provinceId'
  | 'seaportId'
  | 'airportId'
  | 'allCityByCountryId'

type CommonParamsSort = 'nameEn' | 'nameCn'

// params 参数类型
export interface CommonParams {
  current?: number // 当前页
  size?: number // 数量
  level?: number // 级别
  searchContent?: string // 搜索内容
  displayInfo?: DisplayInfo[] // 显示信息
  sort?: CommonParamsSort // nameEn 字段按 a-z 排序 不传默认排序
  prefixSearchContent?: string
  appointSearch?: {
    name: AppointSearchName
    val: string // val 的值需要 JSON.stringify 序列化
  }[] // 特殊查询参数
}

// 响应返回值
interface CommonResponse<T = any> {
  records: T[]
  total: number
  size: number
  current: number
  orders: any[]
  optimizeCountSql: boolean
  searchCount: boolean
  countId: null
  maxLimit: null
  pages: number
}

interface ContinentInterfaceUpperCase {
  continentNameCn: string
  continentId: number
  continentNameEn: string
}

// 国家数据类型
export interface CountryInterface extends ContinentInterfaceUpperCase {
  continentId: number
  telCode: string
  nameCn: string
  sanctionFlag: string
  fullnameEn: string
  id: number
  nameEn: string
  fullnameCn: string
  usedName?: string
  shortCode: string
}
interface CountryInterfaceUpperCase {
  countryContinentId: number
  countryTelCode: string
  countryNameCn: string
  countrySanctionFlag: string
  countryFullnameEn: string
  countryId: number
  countryNameEn: string
  countryFullnameCn: string
  countryUsedName?: string
  countryShortCode: string
}

// 城市数据类型
export interface CityInterface
  extends ContinentInterfaceUpperCase,
    CountryInterfaceUpperCase {
  nameCn: string
  id: number
  nameEn: string
  usedName?: string
  countryId: number
}

interface CityInterfaceUpperCase {
  cityNameCn: string
  cityId: number
  cityNameEn: string
  cityUsedName: string
  cityCountryId: number
}

// 港口数据类型
export interface SeaportInterface
  extends ContinentInterfaceUpperCase,
    CountryInterfaceUpperCase,
    CityInterfaceUpperCase {
  threeCode: string
  nameCn: string
  remark: string
  cityId: number
  id: number
  nameEn: string
  countryId: number
  fiveCode: string
  iataCode: string
  customSearchType?: string
}

// 港口机场类型
export interface SeaportAirportInterface
  extends ContinentInterfaceUpperCase,
    CountryInterfaceUpperCase,
    CityInterfaceUpperCase {
  threeCode: string
  nameCn: string
  remark: string
  cityId: number
  id: number
  nameEn: string
  countryId: number
  fiveCode: string
  customSearchType: 'Seaport' | 'Airport'
}

const BASE_PATH = `/system/dms/fr/aggr/findPageList`

function toUpperCase(key = '') {
  return key.charAt(0).toUpperCase() + key.slice(1)
}

/**
 * 将对象的键值转换为大写，并可选择性地添加前缀
 * 此函数主要用于处理对象的键，将其转换为大写，并且可以选择性地在每个键前面添加一个前缀
 * 这在需要统一键格式或者避免键名冲突时特别有用
 *
 * @param obj - 需要处理的对象，默认为一个空对象
 * @param objKey - 要添加到原键前的前缀，默认为空字符串
 * @returns 返回一个新的对象，其键为原键的大写形式，并添加了指定的前缀
 */
function setObjectValue(obj: StringOfObject<string> = {}, objKey = '') {
  // 获取原对象的所有键名
  const keyList = Object.keys(obj)
  // 初始化一个新的对象来存储处理后的键值对
  const newObj: StringOfObject<string> = {}
  // 遍历原对象的每个键
  for (let i = 0; i < keyList.length; i++) {
    const key = keyList[i]
    // 将处理后的键名（大写并添加前缀）和对应的值存入新对象
    if (key && obj[key] !== undefined) {
      newObj[`${objKey}${toUpperCase(key)}`] = obj[key]
    }
  }
  // 返回处理后的新对象
  return newObj
}

// 1. 基础接口
export async function getBaseData(
  params: CommonParams = {},
): Promise<CommonResponse> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(
    BASE_PATH,
    params,
  )
  return result.data
}

// 2. 获取国家数据
export async function getCountry(
  params: CommonParams = {},
): Promise<CommonResponse<CountryInterface>> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(BASE_PATH, {
    ...params,
    level: 2,
    displayInfo: ['Country'],
  })

  const countryList = result.data.records.map((item: any) => {
    return {
      ...setObjectValue(item.continent, 'continent'),
      ...item.country,
    }
  })
  return {
    ...result.data,
    records: countryList,
  }
}

// 3. 获取城市数据
export async function getCity(
  params: CommonParams = {},
): Promise<CommonResponse<CityInterface>> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(BASE_PATH, {
    ...params,
    level: 3,
    displayInfo: ['City'],
  })

  const cityList = result.data.records.map((item: any) => {
    return {
      ...setObjectValue(item.continent, 'continent'),
      ...setObjectValue(item.country, 'country'),
      ...item.city,
    }
  })

  return {
    ...result.data,
    records: cityList,
  }

  // return await $fetch('/api/city', {
  //   method: 'post',
  //   body: params,
  //   timeout: 120000,
  // })
}

// 6. 获取港口数据
export async function getSeaport(
  params: CommonParams = {},
): Promise<CommonResponse<SeaportInterface>> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(BASE_PATH, {
    ...params,
    level: 4,
    displayInfo: ['Seaport'],
  })
  const seaportList = result.data.records.map((item: any) => {
    return {
      ...setObjectValue(item.continent, 'continent'),
      ...setObjectValue(item.country, 'country'),
      ...setObjectValue(item.city, 'city'),
      ...item.seaport,
    }
  })

  return {
    ...result.data,
    records: seaportList,
  }
}

/**
 * 2025-06-23
 * 获取港口和机场数据
 * @param params
 * @returns
 */
export async function getSeaAndAirPostListApi(
  params: CommonParams = {},
): Promise<CommonResponse<SeaportAirportInterface>> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(BASE_PATH, {
    ...params,
    level: 4,
    displayInfo: ['Airport', 'Seaport'],
  })
  const seaportAirList = result.data.records.map((item: any) => {
    let res = {
      ...setObjectValue(item.continent, 'continent'),
      ...setObjectValue(item.country, 'country'),
      ...setObjectValue(item.city, 'city'),
    }
    if (!_isEmpty(item.seaport)) {
      const subRes = {
        ...item.seaport,
        customSearchType: 'Seaport',
      }
      res = Object.assign(res, subRes)
    }
    if (!_isEmpty(item.airport)) {
      const subRes = {
        ...item.airport,
        customSearchType: 'Airport',
      }
      res = Object.assign(res, subRes)
    }
    return {
      ...res,
    }
  })

  return {
    ...result.data,
    records: seaportAirList,
  }
}

/**
 * 获取船公司数据
 */
export interface ShippingCarrierInterface {
  id: number
  lineType: string
  carrierCode: string
  carrierNameCn: string
  carrierNameEn: string
  carrierFullNameCn: string
  carrierFullNameEn: string
  scacCode: string
  cbsaCode: string
  naccsCode: string
  qdMftCode: string
  iataCode: string
  waybillCode: string
  fileId: string
  webAddress: string
  eori: string
}
export async function getShippingCarrierApi(
  params: CommonParams = {},
): Promise<CommonResponse<ShippingCarrierInterface>> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(
    '/system/dms/fr/carrier/findAllList',
    {
      lineType: 'shipping',
      size: 10,
      current: 1,
      ids: [],
      ...params,
    },
  )

  return {
    ...result.data,
    records: result.data.records.map((item: any) => {
      return {
        ...setObjectValue(item.continent, 'continent'),
        ...setObjectValue(item.country, 'country'),
        ...setObjectValue(item.city, 'city'),
        ...item,
      }
    }),
  }
}

export interface AirCarrierInterface {
  id: number
  lineType: string
  carrierCode: string
  carrierNameCn: string
  carrierNameEn: string
  carrierFullNameCn: string
  carrierFullNameEn: string
  scacCode: string
  cbsaCode: string
  naccsCode: string
  qdMftCode: string
  iataCode: string
  waybillCode: string
  fileId: string
  webAddress: string
  eori: string
}

/**
 * 获取航空公司数据
 */
export async function getAirCarrierApi(
  params: CommonParams = {},
): Promise<CommonResponse<AirCarrierInterface>> {
  const { post } = useUseRequest()
  const result: any = await post<Api.Response<CommonResponse>>(
    '/system/dms/fr/carrier/findAllList',
    {
      lineType: 'air',
      size: 10,
      current: 1,
      ids: [],
      ...params,
    },
  )
  return {
    ...result.data,
    records: result.data.records.map((item: any) => {
      return {
        ...setObjectValue(item.continent, 'continent'),
        ...setObjectValue(item.country, 'country'),
        ...setObjectValue(item.city, 'city'),
        ...item,
      }
    }),
  }
}

// 默认导出
export default {
  getBaseData,
  getCountry,
  getCity,
  getSeaport,
  getShippingCarrierApi,
}
